/**
 * Playano blog posts — single source of truth for the public blog.
 * Sourced from playano-docs/playano/projects/blog/blog01-02 (relocated
 * 2026-08-21 from Raisy — engineering benchmarks that did not promote a
 * Raisy product capability, per RULES_BLOG_POST.md Rule 30).
 * New post = one entry here; the /blog hub and post pages render from this
 * array (newest-first by date).
 */

import * as React from 'react';
import { Link } from 'react-router';
import { RetrievalDepthChart } from '../visualizations/RetrievalDepthChart';
import { RetrievalLatencyChart } from '../visualizations/RetrievalLatencyChart';
import { ContextQualityChart } from '../visualizations/ContextQualityChart';
import { MethodologyLoopChart } from '../visualizations/MethodologyLoopChart';
import { ProductPortfolioChart } from '../visualizations/ProductPortfolioChart';
import { RemedyLagChart } from '../visualizations/RemedyLagChart';
import { AsmlChinaShareChart } from '../visualizations/AsmlChinaShareChart';
import { ImportDependenceChart } from '../visualizations/ImportDependenceChart';
import { DependenceParityChart } from '../visualizations/DependenceParityChart';
import { EdgeConcentrationChart } from '../visualizations/EdgeConcentrationChart';
import { BlogImage } from '@/components/blog/BlogImage';
import { ChartZoom } from '@/components/blog/ChartZoom';

export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  category: string;
  series: string;
  excerpt: string;
  keywords: string[];
  image: string;
  content: React.ReactNode;
  faq?: { q: string; a: string }[];
  sources?: string;
}

export const BlogHubLink = ({ children }: { children: React.ReactNode }) => (
  <Link to="/" className="text-[#FD9800] underline underline-offset-2">
    {children}
  </Link>
);

/** Prefixes a /public asset path with Vite's base URL (this app deploys under /blog/). */
const assetUrl = (path: string) => `${import.meta.env.BASE_URL}${path.replace(/^\//, '')}`;

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: 'the-chokepoint-is-measurable',
    title: 'Your AI Stack: 9% of Imports, 37% of the Rare Earths',
    date: '2026-10-20',
    category: 'Research',
    series: 'The Stack',
    image: assetUrl('blog/stack-chokepoint-cover.jpg'),
    excerpt:
      'China is 9% of U.S. imports and 37% of its rare earths. The headline says the supply chain diversified; the input level says it did not. Here is how to measure your own exposure — and why no court ruling will fix it for you.',
    keywords: [
      'US China tech stack dependence',
      'technology decoupling index',
      'AI supply chain concentration',
      'patent citation dependence',
      'AI antitrust remedy',
    ],
    content: (
      <>
        <p>
          China supplies 9% of everything the United States imports, down from 22% in 2017. It also supplies 37%
          of its rare earth metals, 29% of its magnets and 18% of its network equipment. Both numbers are true at
          the same time, and only one of them describes what an AI buildout actually needs.
        </p>
        <p>
          That gap is the whole problem with how this dependence gets discussed. The aggregate looks like a
          success; the input level is where a product can be stopped. For a studio, the question is narrower
          still: every product we ship has one production input, inference, rented by the token from a small
          number of American vendors, who rent capacity from a supply chain that narrows to a few fabs, which buy
          their equipment from a handful of companies. That is not a metaphor for dependence. It is a bill of
          materials, and a bill of materials can be measured.
        </p>
        <p>
          Two things are usually said about that arrangement. The first is that the dependence is too diffuse to
          quantify. The second is that the antitrust institutions have stood aside while competition became
          available to everyone.
        </p>
        <p>
          The first is false: dependence between the US and Chinese technology stacks has been measured from patent
          data for years. The second is false in a way that is more useful than the version people repeat. The
          institutions are not absent, and they are not deferring to AI. They are winning on liability, losing on
          structure, and the judge in the most-cited case said in plain words that AI has not yet changed the
          market&apos;s dynamics.
        </p>
        <p>
          This is the first post in <strong>The Stack</strong>, a series about the infrastructure Playano builds on.
          It sits alongside our <BlogHubLink>engineering benchmarks</BlogHubLink> and the{' '}
          <BlogHubLink>portfolio overview</BlogHubLink>; the difference is that this one is about supply, not code.
        </p>
        <p>
          <strong>The dependence is measurable at the input level, and the remedy is not coming in time.</strong>
        </p>

        <h2>The problem</h2>
        <p>
          A studio that ships nine AI-native products has an unusual exposure: its marginal cost is set by
          decisions it does not make. When a model vendor changes a price, the unit economics of every product
          change at once. When a government restricts an export, the vendor&apos;s capacity plan changes, and so
          does our delivery schedule. We cannot re-price a product by unilateral decision the way we can change a
          feature.
        </p>
        <p>Two failure modes are worth naming, because they are the ones a studio actually hits.</p>
        <div className="rounded-xl border border-[#333] bg-[#171717] p-5 my-6 space-y-4">
          <p>
            <strong>Pricing shock.</strong> A single provider&apos;s price list is an input to every
            product&apos;s roadmap. The risk is not that inference gets expensive; it is that it gets expensive for
            one product line and not another, so the portfolio&apos;s internal comparison stops meaning anything.
          </p>
          <p>
            <strong>Single-vendor feature.</strong> A capability built on one vendor&apos;s API is a capability we
            can lose. The cost of that dependency does not appear in any invoice until the day it does.
          </p>
        </div>
        <p>
          Both are supply questions. Neither can be answered by reading the news, which is why they are worth
          measuring.
        </p>

        <h2>The idea: two instruments, neither of them a forecast</h2>
        <p>Dependence has two separate properties, and commentary routinely collapses them:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Integration</strong> — how much the two stacks cite, buy from, and build on each other at all.
          </li>
          <li>
            <strong>Asymmetry</strong> — which side leans harder on the other&apos;s knowledge base.
          </li>
        </ul>
        <p>
          They can move in opposite directions, and that is the whole point. A relationship can become more
          integrated while becoming less one-sided.
        </p>

        <h2>How to read a dependence index</h2>
        <p>
          Han, Jiang and Mei build measures of technology decoupling and dependence for the United States and China
          from combined patent data (<em>Management Science</em>, 22 February 2024). Each axis is a citation
          propensity: how likely a patent in one country is to cite the other&apos;s, relative to citing at home.
          The 45-degree line is parity — a point on it means neither side leans harder. Above the line, Chinese
          patents build on U.S. patents more than the reverse: the U.S.-leading region. Below it, the mirror image.
        </p>
        <p>
          The clever part is what happens to a point. Project it onto the parity line and you get two orthogonal
          vectors: the projection along the line measures how much the two systems are integrating at all, and the
          rejection perpendicular to it measures how asymmetrically one leans on the other. Integration and
          dependence become separate axes instead of one contested word.
        </p>
        <ChartZoom>
          <DependenceParityChart />
        </ChartZoom>
        <p>
          Their reported result has a shape most commentary misses. Technology integration rose steadily across the
          first two decades of the century. China&apos;s dependence on the United States is hump-shaped rather than
          monotonic: it rose through the first decade and turned around the end of the Great Recession, in 2009,
          then fell for the rest of the sample. Integration up, dependence down — at the same time.
        </p>
        <p>
          The sector detail matters more than the aggregate for anyone choosing a supplier. Dependence is highest
          in pharmaceuticals, semiconductors, software and smartphones — and falling in all four. The most
          decoupled fields are the newer ones: neural networks, cloud computing, self-driving cars. The older the
          technology, the deeper the entanglement; the newer it is, the more each side built its own.
        </p>
        <p>
          <strong>How sure are we?</strong> The published abstract reports a direction, not a magnitude, and it
          reports no confidence interval. Read it the way a statistician reads any estimate without an interval
          attached: the direction is the finding, the size is unknown. If the effect were absent, the share series
          would show no decade-to-decade trend at all; the paper reports that it does. That is evidence about
          direction. It is no evidence about how large the asymmetry is, and this post does not pretend otherwise.
        </p>

        <h2>Why the aggregate number hides the chokepoint</h2>
        <p>
          The obvious second instrument is import concentration — a Herfindahl-Hirschman index over a country&apos;s
          import basket, where a higher number means a narrower set of suppliers. It is the measure people reach
          for when they want to argue that China remains dependent on foreign inputs.
        </p>
        <p>
          The published series says something more interesting than that argument needs. Lovely and Yan, writing
          for the Peterson Institute in August 2024, find that both China and the United States{' '}
          <em>reduced</em> their sourcing concentration between 2018 and 2023 — a reversal after the growth between
          2013 and 2018.
        </p>
        <h3>Figure 1 — the aggregate against the inputs</h3>
        <ChartZoom>
          <ImportDependenceChart />
        </ChartZoom>
        <p>
          The same pattern shows up in the trade data. J.P. Morgan puts China at 9% of all U.S. imports in 2025,
          down from 22% in 2017 — a headline that reads as diversification achieved. Within the categories an AI
          buildout actually consumes, the shares are 37% for rare earth metals, 29% for magnets, 18% for network
          equipment, and only 3% for integrated circuits and semiconductors.
        </p>
        <p>
          That is not a contradiction of the chokepoint story. It is a warning about the instrument. An aggregate
          index falls when a country buys more categories from more places; it says almost nothing about a single
          critical input, which is where a chokepoint lives. If you want to know whether you can be cut off, the
          aggregate index is the wrong number, and it will tell you dependence is falling at precisely the moment
          one input becomes irreplaceable.
        </p>

        <h2>The antitrust record: liability yes, structure no</h2>
        <p>
          Now the second half of the conventional story — that enforcers have effectively stepped aside.
        </p>
        <p>
          They have not. On 5 August 2024, Judge Amit Mehta held that Google violated Section 2 of the Sherman Act
          by maintaining a monopoly in general search through exclusionary conduct. On 2 September 2025 he issued
          the remedy. Google does not have to divest Chrome, and does not have to divest Android. It is barred from
          exclusive distribution contracts, must share search index and user-interaction data with qualified
          competitors (though not ads data), must offer search and search text ads syndication, must disclose
          material ad-auction changes, and is overseen by a technical committee for six years.
        </p>
        <p>
          The popular explanation for the lighter remedy is that courts bought the argument that AI changes the
          monopoly math. The opinion says close to the opposite. Mehta rejected that defence directly:
        </p>
        <blockquote className="border-l-2 border-[#FD9800] pl-4 my-6 text-gray-300 italic">
          &ldquo;AI has unquestionably improved general search, but it has not yet fundamentally altered market
          dynamics.&rdquo;
        </blockquote>
        <p>
          He then extended the decree to cover generative AI against Google&apos;s wishes, because on that point
          &ldquo;the evidence overwhelmingly points the other way.&rdquo; Where AI did lighten the remedy was
          narrower and more defensible: he cut the decree&apos;s term from the ten years plaintiffs requested to
          six, because a longer order risked &ldquo;growing stale in these fast-moving times, where GenAI
          technologies are breaking barriers seemingly at light speed.&rdquo;
        </p>
        <p>
          So the leniency did not come from a belief that AI has already restored competition. It came from the
          legal test for structural relief — the requirement of a clear causal connection between the monopoly and
          the harm — and from a judgement about how long a remedy can usefully run.
        </p>
        <p>
          The Meta case closes the loop. On 18 November 2025, Chief Judge James Boasberg found that the FTC had not
          carried its burden: &ldquo;Meta holds no monopoly in the relevant market,&rdquo; because TikTok and
          YouTube are genuine substitutes for users&apos; time. AI does not appear in the reasoning as a
          competitive constraint at all. The FTC has appealed, and Instagram and WhatsApp remain inside Meta while
          the appeal runs.
        </p>

        <h3>What the court granted, and what it refused</h3>
        <div className="overflow-x-auto my-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-[#333]">
                <th className="text-left py-2 pr-3 text-gray-200">Remedy in U.S. v. Google</th>
                <th className="text-left py-2 pr-3 text-gray-200">Outcome, 2 Sep 2025</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-[#222]">
                <td className="py-2 pr-3">Divestiture of Chrome</td>
                <td className="py-2 pr-3 text-gray-400">Refused</td>
              </tr>
              <tr className="border-b border-[#222]">
                <td className="py-2 pr-3">Divestiture of Android</td>
                <td className="py-2 pr-3 text-gray-400">Refused</td>
              </tr>
              <tr className="border-b border-[#222]">
                <td className="py-2 pr-3">Exclusive distribution contracts</td>
                <td className="py-2 pr-3 text-[#FD9800]">Barred</td>
              </tr>
              <tr className="border-b border-[#222]">
                <td className="py-2 pr-3">Search index and interaction data to competitors</td>
                <td className="py-2 pr-3 text-[#FD9800]">Required (not ads data)</td>
              </tr>
              <tr className="border-b border-[#222]">
                <td className="py-2 pr-3">Search and search-text-ads syndication</td>
                <td className="py-2 pr-3 text-[#FD9800]">Required</td>
              </tr>
              <tr className="border-b border-[#222]">
                <td className="py-2 pr-3">Disclosure of ad-auction changes</td>
                <td className="py-2 pr-3 text-[#FD9800]">Required</td>
              </tr>
              <tr className="border-b border-[#222]">
                <td className="py-2 pr-3">Payment for default placement</td>
                <td className="py-2 pr-3 text-gray-400">Declined &ldquo;at this time&rdquo;</td>
              </tr>
              <tr>
                <td className="py-2 pr-3">Term of the decree</td>
                <td className="py-2 pr-3 text-gray-400">Six years, not ten</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Figure 3 — the remedy lag</h3>
        <p>
          The pattern across the litigated cases is not that institutions abstain. It is that the remedy arrives
          after the market has already moved, and that structure usually survives the arrival.
        </p>
        <ChartZoom>
          <RemedyLagChart />
        </ChartZoom>
        <p>
          Two corrections matter here, because the loose version of this argument is wrong twice. AT&amp;T did end
          in a breakup, by consent — but the consent was filed mid-trial, in January 1982, after five months of
          testimony, so it settles a litigated case rather than replacing one. And Microsoft did produce a
          litigated breakup order, in June 2000; the D.C. Circuit vacated it a year later, and the case closed on
          conduct remedies. The accurate statement is narrower and still damning: a monopoly was broken up once, it
          took nine years, and that was forty years ago.
        </p>

        <h2>What the fear is actually about</h2>
        <p>
          If the institutions are not the constraint, the fear lives somewhere else: in losing the levers that made
          the stack dominant, and in what those levers cost to keep pulling.
        </p>

        <h3>The chokepoint leaks</h3>
        <p>
          On 14 October 2026, ASML&apos;s chief financial officer, Roger Dassen, called the MATCH Act — a US
          congressional proposal to restrict ASML&apos;s DUV lithography sales to China — a bad idea, saying it
          would accelerate China&apos;s development of competing technology. The same quarter&apos;s numbers show
          why the argument is not abstract.
        </p>

        <h3>Figure 4 — ASML&apos;s China share of equipment sales</h3>
        <ChartZoom>
          <AsmlChinaShareChart />
        </ChartZoom>
        <p>
          China&apos;s share of ASML equipment sales rose from 14% in the second quarter of 2026 to 23% in the
          third — and was 42% a year earlier. The lever moves in both directions, and pulling it costs the company
          holding the lever. A restriction that accelerates the capability it was meant to delay is not a
          chokepoint; it is a timer.
        </p>

        <h3>The cost structure, not the capability</h3>
        <p>
          On 12 October 2026, at a panel in Singapore, Intel&apos;s chief executive Lip-Bu Tan said adoption of
          Chinese frontier models had been &ldquo;very fast,&rdquo; citing their lower token costs, and that models
          such as DeepSeek, Kimi and GLM were performing well enough for many applications.
        </p>
        <p>
          That is the sentence a software house should read twice. The competitive threat is not a model that is
          better. It is a model that is good enough and cheaper per token — because a studio&apos;s margin is the
          gap between what a product is worth and what its inference costs.
        </p>

        <h2>So we measured our own</h2>
        <p>
          Everything above is other people&apos;s measurement of a national relationship. The version that matters
          for us is smaller and fully ours: which model vendors do our repositories actually declare, and where do
          those vendors terminate on the network? Both questions can be answered from one machine with no vendor
          data and no API keys, so we wrote the script and committed it with its output.
        </p>
        <p>
          The software half is a scan of twelve repositories for declared AI SDKs. Seven declare at least one
          provider across fourteen mentions. OpenAI appears in five of them, Google in three, and Anthropic and
          Mistral in two each. Measured as a Herfindahl index over those mentions, provider concentration is
          0.23 — diversified enough that no single vendor is the whole story, and concentrated enough that one
          vendor&apos;s pricing change reaches most of the portfolio.
        </p>
        <p>
          The network half is the one we had not seen published anywhere. For each provider endpoint we resolve
          the address, look up the autonomous system that owns it, and count the hops. Two facts came back. First,
          the path is the same length to almost everyone: eight to ten hops, whether the vendor is American or
          Chinese. Second, the paths are not independent.
        </p>
        <ChartZoom>
          <EdgeConcentrationChart />
        </ChartZoom>
        <p>
          Four of the seven endpoints terminate in a single network — Cloudflare&apos;s. OpenAI, OpenRouter,
          Mistral and Groq all sit behind the same edge. Anthropic owns its own ASN, Google uses its own, and
          DeepSeek&apos;s endpoint resolves into Amazon. That does not mean four vendors share one failure mode for
          model capacity; Cloudflare terminating TLS tells us nothing about whose GPUs answer. It means our
          four-vendor hedge is a one-vendor hedge at the layer we can actually observe, and that a routing or
          certificate incident at one network reaches most of our portfolio at once.
        </p>
        <p>
          The honest limit is the interesting part. A traceroute measures the edge in front of a provider, not the
          provider&apos;s supply. So this instrument answers &ldquo;how many independent paths do we have?&rdquo;
          and not &ldquo;can our inference be cut off?&rdquo; For the second question the only real measurement is
          spend: what share of the bill, and what share of shipped features, depend on one vendor. That number we
          are still building the telemetry for, and this post will not claim it.
        </p>

        <h2>What we do about it</h2>
        <p>
          None of this is actionable as commentary. As operating practice, it converts into three numbers we now
          track for every product:
        </p>
        <ol className="list-decimal pl-6 space-y-2">
          <li>
            <strong>Share of inference spend by provider.</strong> One number per product, updated monthly. A
            portfolio where one vendor is 80% of spend is a portfolio with one supplier.
          </li>
          <li>
            <strong>Share of features that only run on one provider&apos;s API.</strong> Counted, not estimated.
            This is the number that turns &ldquo;we could switch&rdquo; into a schedule.
          </li>
          <li>
            <strong>Cost per unit of output, and its trend.</strong> The same unit across products, so a price
            change in one vendor shows up as a portfolio-level movement rather than a surprise in a monthly close.
          </li>
        </ol>
        <p>
          Two rules follow from the evidence above rather than from caution. <strong>Portability is a hedge, not
          elegance</strong> — the decree that would protect a competitor arrives six years late, if it arrives. And{' '}
          <strong>plan as if the remedy never comes</strong>: any part of the business that only works because a
          regulator eventually wins is a part of the business that is not yet designed.
        </p>
        <p>
          That is the useful half of the fear. The dependence can be watched, and watching it is cheaper than being
          surprised by it.
        </p>

        <h2>What this post does not answer</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>The size of the dependence.</strong> The published result is directional; the full text was not
            available to this post, so no magnitude and no confidence interval are reported here.
          </li>
          <li>
            <strong>Input-level concentration.</strong> No public series was found for import concentration on
            advanced semiconductors specifically. The aggregate index is not a substitute.
          </li>
          <li>
            <strong>Whether the pattern generalises.</strong> Two litigated cases and one consent decree are not a
            trend. Reading them as one would be the same error this post criticises in others.
          </li>
          <li>
            <strong>Enforcement outside the United States.</strong> The EU&apos;s and China&apos;s own competition
            authorities are out of scope.
          </li>
          <li>
            <strong>What an actual interruption would cost us.</strong> This post measures exposure; it does not
            model a shock.
          </li>
        </ul>

        <h2>Takeaway</h2>
        <p>
          Dependence between the two stacks is real, measurable, and moving in two directions at once: integration
          up, asymmetry down. The institutions that are supposed to police concentration are neither absent nor
          deferential — they are winning liability and losing structure, and the AI argument that was supposed to
          explain the leniency was rejected in the opinion that granted it. A six-year conduct decree and a 23%
          equipment share are what the levers actually look like in practice.
        </p>
        <p>
          For a studio, that leaves one honest conclusion: measure your own exposure, because nobody else&apos;s
          remedy will arrive in time to do it for you. The nine products that exposure runs through are described
          in{' '}
          <a href="https://playanoedu.com/blog/the-playano-portfolio" className="text-[#FD9800] underline underline-offset-2">
            The Playano Portfolio
          </a>
          .
        </p>
      </>
    ),
    faq: [
      {
        q: 'Why is a software studio writing about geopolitics?',
        a: 'Because inference is a purchased input with a supply chain. When a vendor reprices or a government restricts an export, our cost base and our delivery schedule move. This series measures that exposure rather than reacting to headlines about it.',
      },
      {
        q: 'Did the court go easy on Google because of AI?',
        a: 'No — the opposite, on the AI point. Judge Mehta wrote that AI had not yet fundamentally altered market dynamics, and extended the decree to cover generative AI. AI shortened the decree from ten years to six; it did not cause the refusal to order a divestiture.',
      },
      {
        q: 'Is China still dependent on foreign technology?',
        a: 'The patent-citation evidence says its dependence on the US rose in the 2000s and fell in the 2010s, while overall integration kept rising. Aggregate import concentration fell after 2018, which is why the aggregate measure is the wrong instrument for a single critical input.',
      },
      {
        q: 'What should a small team do with this?',
        a: 'Track three numbers per product: share of inference spend by provider, share of features that only run on one provider, and cost per unit of output. If you cannot produce those three, you do not yet know your exposure.',
      },
    ],
    sources:
      'United States v. Google LLC, No. 20-cv-3010 (APM) (D.D.C.): liability opinion 5 Aug 2024 (747 F. Supp. 3d 1); remedy memorandum opinion 2 Sep 2025. FTC v. Meta Platforms, Inc., No. 20-3590 (JEB) (D.D.C.), memorandum opinion 18 Nov 2025 (Chief Judge James E. Boasberg); appeal pending, D.C. Cir. No. 26-5028. Han, Jiang & Mei, "Mapping U.S.-China Technology Decoupling: Policies, Innovation, and Firm Performance," Management Science, 22 Feb 2024, doi:10.1287/mnsc.2022.02057. Lovely & Yan, "While the US and China decouple, the EU and China deepen trade dependencies," PIIE, 27 Aug 2024. ASML Q3 2026 results, 14 Oct 2026, and CFO Roger Dassen on the MATCH Act. Lip-Bu Tan, DBS panel, Singapore, 12 Oct 2026, as reported by Reuters. All figure values are committed in src/components/data/stackDependence.json. Figures 1 and 4 are J.P. Morgan and ASML figures as published; Figures 3 and 5 are our own measurement, produced by a committed script (playano/tools/measure_stack_dependence.py) whose raw output is src/components/data/stackSelfMeasurement.json — rerun it to reproduce the counts, ASNs and hop numbers.',
  },
  {
    slug: 'the-playano-portfolio',
    title: 'The Playano Portfolio: One Studio, Eight Products',
    date: '2026-08-21',
    category: 'Vision',
    series: 'Playano Studio',
    image: assetUrl('blog/playano-portfolio-cover.jpg'),
    excerpt:
      'Playano is not a holding company that happens to own apps. It is one AI-native studio methodology, applied to eight different problems. Here is what we have shipped, and where to read more about each one.',
    keywords: ['Playano product portfolio', 'AI-native studio', 'Playano products', 'David Demmer Playano'],
    content: (
      <>
        <p>
          Playano Education Limited is not a holding company that happens to own a handful of apps. It is one
          methodology &mdash; humans set direction and review, AI executes and iterates &mdash; applied to eight
          different problems. This post is the front door: what exists today, what does not, and where to read more
          about each one.
        </p>

        <h2>The problem this post solves</h2>
        <p>
          A visitor, investor, or press contact who lands on playanoedu.com sees eight product cards side by side,
          with no sense of which ones are live, which are shut down, and which methodology ties them together. This
          post is that missing map. It is also the one page every future Playano post &mdash; on this blog or on any
          subsidiary&apos;s own blog &mdash; can point back to instead of re-explaining the portfolio from scratch.
        </p>

        <ChartZoom><ProductPortfolioChart /></ChartZoom>

        <h2>Active products</h2>
        <ul className="list-disc pl-6 space-y-3">
          <li>
            <strong>Raisy</strong> &mdash; a 20-role AI workforce for company operations: fundraising, sales,
            recruiting, and more, with full human oversight and earned autonomy. Has its own blog at{' '}
            <a href="https://raisy.team/blog" className="text-[#FD9800] underline underline-offset-2">
              raisy.team/blog
            </a>
            .
          </li>
          <li>
            <strong>Glowtest</strong> &mdash; an AI-powered video campaign generation system.
          </li>
          <li>
            <strong>Alphakey</strong> &mdash; AI-powered investment research and opportunity detection.
          </li>
          <li>
            <strong>Mapster</strong> &mdash; turns user insights into actionable development roadmaps.
          </li>
          <li>
            <strong>Flixn</strong> &mdash; a premium micro-drama platform with 90-second vertical episodes.
          </li>
          <li>
            <strong>Promptforge</strong> &mdash; the AI Studio OS that runs Playano&apos;s own video production
            pipeline, from prompt to profit.
          </li>
        </ul>

        <h2>Retired or paused</h2>
        <p>
          Two products are not part of the active roster. <strong>AIN-network</strong> (AI-generated news and content)
          is currently offline. <strong>Piano Hero</strong> (learn piano faster with AR) is a legacy app, no longer
          supported. Both are listed here for completeness, not as something to try today.
        </p>

        <h2>The one methodology behind all of it</h2>
        <p>
          Every product above runs the same loop: a human sets the strategy and reviews the output, AI does the
          production, iteration, and scaling in between. We wrote the full case for why this is a methodology, not
          just a tagline, in{' '}
          <Link to="/playano-ai-native-methodology" className="text-[#FD9800] underline underline-offset-2">
            The Playano Loop: AI-Native as a Method, Not a Slogan
          </Link>
          .
        </p>

        <h2>Why this matters</h2>
        <p>
          A studio that reuses one methodology across eight problems has a different kind of leverage than eight
          unrelated apps under one logo. A lesson learned building Raisy&apos;s human-approval gate is the same
          lesson that shapes how Glowtest reviews a generated campaign, or how Promptforge gates a video before it
          ships. This post exists so that leverage is visible from the outside, not just felt internally.
        </p>

        <h2>What this post does not answer</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>It does not carry product-level metrics (revenue, users, traction) for any subsidiary &mdash; those belong on that product&apos;s own site or blog, not here.</li>
          <li>It does not rank or score the products against each other &mdash; no such measurement exists, and inventing one would violate our own no-invented-data standard.</li>
          <li>Blog links are current as of the date on this post; a product that ships its own blog after this date will not automatically appear until this post is updated (see the changelog convention on the companion methodology post).</li>
        </ul>

        <p className="mt-8">
          <a href="https://playanoedu.com" className="inline-block bg-[#FD9800] hover:bg-[#e08700] text-black font-semibold px-6 py-3 rounded-lg">
            See the full product suite at playanoedu.com
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is Playano a holding company or a product studio?', a: 'A studio. Every product runs the same human-in-the-loop, AI-in-the-flow methodology — it is not a passive investment vehicle.' },
      { q: 'Which products are actually live today?', a: 'Raisy, Glowtest, Alphakey, Mapster, Flixn, and Promptforge. AIN-network is offline and Piano Hero is a legacy app, no longer supported.' },
      { q: 'Does every product have its own blog?', a: 'Not yet. Raisy has a live blog today. Other products are linked to their product page until they publish their own.' },
      { q: 'Who founded Playano?', a: 'David Gerhard Demmer (CEO & CTO) and Simon Stegelmeier (CMO) co-founded Playano.' },
    ],
    sources:
      'Product names, descriptions, and status: Playano_Ed_HP_4/public/index.html ("Our Product Suite" section), as of 2026-08-21. Founder titles: same file, "Our Team" section.',
  },
  {
    slug: 'playano-ai-native-methodology',
    title: 'The Playano Loop: AI-Native as a Method, Not a Slogan',
    date: '2026-08-20',
    category: 'Vision',
    series: 'Playano Studio',
    image: assetUrl('blog/playano-methodology-cover.jpg'),
    excerpt:
      'Humans provide input and feedback. Everything else — execution, iteration, optimization, scaling — is automated. Here is the loop behind every product Playano ships, and why it is a method, not a tagline.',
    keywords: ['AI-native product studio', 'human in the loop AI', 'AI-native methodology', 'Playano methodology'],
    content: (
      <>
        <p>
          &quot;AI-native&quot; gets used as a marketing adjective more often than it gets used as an actual method.
          At Playano, it names one specific loop, applied the same way across every product we build: humans provide
          input and feedback; execution, iteration, optimization, and scaling are automated.
        </p>

        <h2>The problem(s) we started with</h2>
        <p>
          Most companies that call themselves &quot;AI-native&quot; mean one of two things: they use an LLM API
          somewhere in the stack, or their marketing copy says so. Neither is a method. A method has to answer a
          concrete question every time a task shows up: what does a human have to do here, and what can the system do
          on its own? Without an explicit answer, &quot;AI-native&quot; either means nothing, or it quietly means
          &quot;no human ever checks this,&quot; which is a much riskier claim than most teams intend to make.
        </p>
        <p>
          The second problem is consistency across a portfolio. We build six active products, in different domains
          (video, investment research, roadmapping, workforce automation, content). Without one shared method, each
          product team reinvents its own answer to &quot;how much do we automate,&quot; and the lessons one team
          learns never reach the others.
        </p>

        <ChartZoom><MethodologyLoopChart /></ChartZoom>

        <h2>The idea</h2>
        <p>
          The loop has four stops, and the same four stops apply whether the product is generating a video campaign,
          researching an investment thesis, or drafting an investor email. <strong>Human Input</strong> sets strategy,
          creative direction, and judgment calls that require context a model does not have. <strong>AI
          Produce</strong> does the execution: generation, analysis, iteration, at machine speed and machine scale.
          <strong> Human Review</strong> validates or corrects the output. <strong>Ship</strong> releases the
          approved work &mdash; and the correction from Human Review feeds directly back into the next AI Produce
          cycle, so the system gets better at exactly the thing a human just had to fix.
        </p>

        <h2>How it works across the portfolio</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Raisy</strong> applies this to company operations: a bot prepares an investor email or a sales draft (AI Produce), a human approves or edits it (Human Review) before it ever reaches an external inbox (Ship).</li>
          <li><strong>Glowtest</strong> applies it to video campaigns: creative direction and brand voice are Human Input, generation and iteration are AI Produce, and a marketer signs off before a campaign goes live.</li>
          <li><strong>Alphakey</strong> applies it to investment research: a thesis or watchlist is Human Input, the AI does the detection and analysis, and a human still makes the call before capital moves.</li>
        </ul>
        <p>
          The specific inputs, outputs, and review criteria differ by product. The shape of the loop does not.
        </p>

        <h2>What this looks like day to day</h2>
        <p>
          A team member sets direction once at the start of a project, then spends their time reviewing and
          correcting output instead of producing it by hand. A correction made this week is not a one-off fix &mdash;
          it is signal the system uses on the next cycle, so the same category of mistake gets caught earlier next
          time, across whichever product it happened in.
        </p>

        <h2>Why this matters beyond convenience</h2>
        <p>
          A company that automates execution without a real review step is not AI-native, it is unsupervised, and
          that is a different and much riskier thing to be. A company that requires a human to do the execution
          itself is not AI-native either, it is just a tool user. The loop is the actual claim underneath the label:
          humans keep judgment, AI keeps the toil, and the two stay connected through the same feedback path on every
          product we ship. (See the full product list this method is applied to in{' '}
          <Link to="/the-playano-portfolio" className="text-[#FD9800] underline underline-offset-2">
            The Playano Portfolio: One Studio, Eight Products
          </Link>
          , and browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>What this post does not answer</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>It does not quantify how much time or cost the loop saves versus a fully manual process — that number would need to be measured per product, not asserted at the studio level.</li>
          <li>It does not claim every product has identical automation depth today; the loop is the shared target shape, not a claim that every product has reached full parity in how much of "AI Produce" is actually automated yet.</li>
          <li>It does not cover governance mechanics (confidence scores, autonomy tiers, audit logs) in detail — those are implemented per product; Raisy&apos;s own blog documents its specific version of this in depth.</li>
        </ul>

        <p className="mt-8">
          <a href="https://playanoedu.com" className="inline-block bg-[#FD9800] hover:bg-[#e08700] text-black font-semibold px-6 py-3 rounded-lg">
            See the method applied across our products at playanoedu.com
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is "AI-native" just a rebrand of "uses AI"?', a: 'No — it names a specific loop (human input, AI execution, human review, ship with feedback) that we apply the same way across every product, not just an API integration.' },
      { q: 'Does this mean no human touches the output?', a: 'The opposite. Human Review is a mandatory stop in the loop on every product, not an optional add-on.' },
      { q: 'Is the automation depth the same across every product?', a: 'No — the loop is the shared shape; how much of "AI Produce" is actually automated today varies by product and matures over time.' },
      { q: 'Where can I see this applied to a specific product?', a: 'Raisy documents its own version of this (the human-approval gate, confidence scores, audit trail) in depth on its own blog — see the portfolio post for the link.' },
    ],
    sources:
      'Methodology description and loop diagram: Playano_Ed_HP_4/public/index.html, "Our Methodology" section, as of 2026-08-21.',
  },
  {
    slug: 'rag-context-injection-benchmark',
    title: 'RAG Context Injection Benchmark: Full-File vs Retrieval',
    date: '2026-08-11',
    category: 'Engineering',
    series: 'Playano Engineering',
    image: assetUrl('blog/fig1_context_vs_quality.png'),
    excerpt:
      'We measured four ways to give an LLM internal rules: no context, full-file paste, default RAG, and optimized RAG. Full-file context was 97% noise; optimized RAG matched its quality at 8x the information ratio.',
    keywords: ['RAG context injection benchmark', 'LLM context window cost', 'retrieval vs full context', 'token efficiency RAG', 'LLM context strategy'],
    content: (
      <>
        <p>
          When you want an LLM to follow your internal rules, the cheapest option is also the worst: give it nothing.
          The most obvious fix &mdash; paste the entire rulebook into the prompt &mdash; can cost nearly ninety times
          more tokens and still produce the same generic answer. We ran a controlled benchmark on a real internal
          task and found that the full-file approach was <strong>97% noise</strong>. The winning variant matched its
          quality with <strong>8&times; the information ratio</strong>, and the only difference between the two RAG
          variants was the query.
        </p>

        <h2>The problem</h2>
        <p>
          Every AI team eventually faces the same choice. You have a library of internal rules, runbooks, and project
          docs. You want an LLM to use them. Do you dump the whole library into every prompt, retrieve only the parts
          that match the current task, or retrieve smarter &mdash; with role, intent, and context baked into the
          query? The first is easy to implement and expensive to run. The second saves tokens but can return the
          wrong rules. The third requires discipline. We wanted numbers for all three, measured on the same task,
          with the same model, at the same temperature.
        </p>
        <p>
          The task: draft a short, personalized cold outreach message to a founder who just announced a seed round.
          The rules live in an internal documentation set, indexed by a production retrieval service. We compared
          four context-injection strategies.
        </p>

        <h2>What we measured</h2>
        <p>
          We ran the benchmark on the cold-outreach task using a fixed model at a fixed temperature. Each output was
          scored by a separate LLM judge against an 8-dimension rubric (0&ndash;3 per dimension, max 24).
        </p>

        <table className="w-full text-sm border-collapse my-6">
          <thead>
            <tr className="border-b border-[#333]">
              <th className="text-left py-2 pr-3">Variant</th>
              <th className="text-left py-2 pr-3">Context strategy</th>
              <th className="text-right py-2 pr-3">Quality / 24</th>
              <th className="text-right py-2 pr-3">Total tokens</th>
              <th className="text-right py-2 pr-3">Context tokens</th>
              <th className="text-right py-2">Info ratio (quality / 1K tokens)</th>
            </tr>
          </thead>
          <tbody className="text-gray-300">
            <tr className="border-b border-[#222]">
              <td className="py-2 pr-3 font-mono text-xs">none</td>
              <td className="py-2 pr-3">System prompt only</td>
              <td className="py-2 pr-3 text-right">18</td>
              <td className="py-2 pr-3 text-right">198</td>
              <td className="py-2 pr-3 text-right">42</td>
              <td className="py-2 text-right">428.6</td>
            </tr>
            <tr className="border-b border-[#222]">
              <td className="py-2 pr-3 font-mono text-xs">local</td>
              <td className="py-2 pr-3">Full rules file pasted in</td>
              <td className="py-2 pr-3 text-right">19</td>
              <td className="py-2 pr-3 text-right">18,096</td>
              <td className="py-2 pr-3 text-right">17,934</td>
              <td className="py-2 text-right">1.1</td>
            </tr>
            <tr className="border-b border-[#222]">
              <td className="py-2 pr-3 font-mono text-xs">rag</td>
              <td className="py-2 pr-3">Top-5 chunks, default query</td>
              <td className="py-2 pr-3 text-right">18</td>
              <td className="py-2 pr-3 text-right">2,384</td>
              <td className="py-2 pr-3 text-right">2,232</td>
              <td className="py-2 text-right">8.1</td>
            </tr>
            <tr>
              <td className="py-2 pr-3 font-mono text-xs">rag_optimized</td>
              <td className="py-2 pr-3">Top-5 chunks, role-aware query</td>
              <td className="py-2 pr-3 text-right">19</td>
              <td className="py-2 pr-3 text-right">2,303</td>
              <td className="py-2 pr-3 text-right">2,134</td>
              <td className="py-2 text-right">8.9</td>
            </tr>
          </tbody>
        </table>

        <p>
          The full-file variant cost <strong>427&times; more context tokens</strong> than the no-context baseline and
          bought exactly one quality point. The optimized RAG variant matched that quality point with{' '}
          <strong>97% fewer tokens</strong> than the full file.
        </p>
        <p>
          <em>
            One task, one model, one run per variant &mdash; these are point estimates, not statistically tested
            differences; see &quot;What this post does not answer&quot; below.
          </em>
        </p>

        <ChartZoom><ContextQualityChart /></ChartZoom>

        <h2>What the retrieved context actually was</h2>
        <p>
          The master rules file is 17,934 tokens. A rules-used analysis estimated that only about 500 tokens were
          relevant to a cold email &mdash; roughly 3%. The rest was unrelated engineering, governance, and operations
          material. The output was effectively the same email the no-context variant produced, just with a slightly
          tighter sign-off.
        </p>
        <p>
          The default RAG query retrieved fundraising playbook material: one genuinely useful rule on cold-to-warm
          conversion, but also a rule that directly contradicts the task (&quot;never cold email until you&apos;ve
          exhausted warm intro paths&quot;). The model ignored the contradiction, but three of the five retrieved
          chunks were mismatched to the task.
        </p>
        <p>
          The optimized query &mdash; naming the mechanism (&quot;outbound sales,&quot; &quot;cold email
          etiquette,&quot; &quot;personalized pitch&quot;) rather than the task&apos;s own framing words &mdash;
          retrieved different material: sales etiquette and pitch rules that visibly shaped the final message. Useful
          token share rose from roughly 3% (full file) to roughly 30% (optimized RAG), and the output avoided the
          contradictory rule entirely.
        </p>

        <BlogImage
          src={assetUrl("blog/fig2_signal_to_noise.png")}
          alt="Signal-to-noise in retrieved context across four variants"
          caption="Figure 2. Useful vs wasted context tokens per variant. The full-file prompt was about 97% noise; the optimized RAG prompt was about 30% useful."
        />

        <BlogImage
          src={assetUrl("blog/fig3_information_ratio.png")}
          alt="Information ratio by context-injection variant"
          caption="Figure 3. Quality points per 1,000 context tokens. Optimized RAG delivers roughly 8x the information ratio of the full-file approach. This is a cost-normalization framing, not an industry-standard metric."
        />

        <h2>The idea</h2>
        <p>
          Context injection is a measured engineering decision, not a default. The right amount of context depends on
          the task, the role, and the query. A full-file paste is rarely optimal because most of the file is
          irrelevant to the specific question. A well-formed retrieval query can find the right guidance at a
          fraction of the token cost.
        </p>
        <p>
          The key finding is that <strong>query terms should match procedure and etiquette rules, not the structure
          of the task</strong>. The default query used the task&apos;s own framing words and matched
          content/structure rules. The optimized query used mechanism terms and matched the rules that actually teach
          how to write the email.
        </p>

        <BlogImage
          src={assetUrl("blog/fig4_radar_rubric.png")}
          alt="Rubric dimension scores across four context-injection variants"
          caption="Figure 4. Per-dimension judge scores for all four variants. The biggest differentiator is personalization, where optimized RAG scored higher by retrieving rules that emphasize single-recipient, customized outreach."
        />

        <h2>What engineers actually see</h2>
        <p>
          Running this benchmark gets you one command and a report. The harness logs every input, output, token
          count, file used, chunk retrieved, and per-dimension judge score, and flags cases where full-file loses to
          no context or where the optimized query loses to the default. You get a quantified answer to &quot;how much
          context is enough for this task?&quot; The winning variant is not the one with the most context &mdash; it
          is the one with the most relevant context.
        </p>

        <BlogImage
          src={assetUrl("blog/fig5_multi_metric.png")}
          alt="Quality, tokens, latency, and information ratio across four variants"
          caption="Figure 5. Quality, context tokens, latency, and information ratio side by side for all four variants."
        />

        <BlogImage
          src={assetUrl("blog/fig6_quality_per_dollar.png")}
          alt="Quality vs estimated cost across four context-injection variants"
          caption="Figure 6. Cost-quality scatter using per-token pricing. Optimized RAG sits at the quality frontier for a fraction of the full-file cost."
        />

        <h2>Why this matters</h2>
        <p>
          Context tokens are not just a cost problem. They are a noise problem. Every irrelevant rule in the prompt
          is a chance for the model to fixate on the wrong guidance, contradict the user, or dilute the answer.
          Retrieval discipline compounds: better retrieval means better preparation and faster review, whatever the
          system built on top of it. (See the companion post on{' '}
          <Link to="/rag-retrieval-methods-benchmark" className="text-[#FD9800] underline underline-offset-2">
            how much to retrieve in the first place
          </Link>
          , and browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>What this post does not answer</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>We tested one task (cold outreach) on one generation model, at one run per variant &mdash; no repeated
            trials, no confidence interval. Treat every number above as a single-sample point estimate, not a
            statistically tested difference.</li>
          <li>We did not test keyword-only versus hybrid versus dense retrieval; the production retrieval service
            currently uses hybrid search.</li>
          <li>The 8-dimension rubric is task-specific. A different task would need a different rubric, and the
            quality rankings could change.</li>
          <li>Information ratio is our own cost-normalization framing, not an industry-standard metric. It
            complements, but does not replace, retrieval metrics like precision@k and generation metrics like RAGAS
            faithfulness.</li>
          <li>Results may differ for code-generation, long-document summarization, or multi-turn conversations.</li>
        </ul>

        <p className="mt-8">
          <a href="https://playanoedu.com" className="inline-block bg-[#FD9800] hover:bg-[#e08700] text-black font-semibold px-6 py-3 rounded-lg">
            Read more engineering benchmarks at playanoedu.com
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is pasting the whole file ever the right choice?', a: 'Rarely. In this benchmark it cost 427x more context tokens than no context at all for one extra quality point. It can make sense for very short reference files where retrieval overhead exceeds the file size.' },
      { q: 'Why not just always use the biggest k in retrieval?', a: 'More retrieved chunks means more tokens and more chances to retrieve an irrelevant or contradictory rule. The optimized-query variant beat a larger, less targeted retrieval by matching the mechanism, not the task wording.' },
      { q: 'What is "information ratio"?', a: 'Quality points per 1,000 context tokens — a cost-normalized framing for "how much answer quality did we buy per unit of context." It is not a substitute for standard retrieval metrics like precision@k or recall@k.' },
      { q: 'Does this generalize beyond cold email?', a: 'Untested here. The single-task, single-model, single-run design means this is a documented case, not a general law — see "What this post does not answer."' },
    ],
    sources:
      'Internal RAG benchmark harness, run 2026-08-11, against a production retrieval service. Generation and judging model held fixed at temperature 0.3. Single sample per variant — no repeated trials or confidence interval computed; see "What this post does not answer."',
  },
  {
    slug: 'rag-retrieval-methods-benchmark',
    title: 'RAG Retrieval Methods Benchmark: LOOKUP vs AUDIT vs SYNTHESIS',
    date: '2026-08-11',
    category: 'Engineering',
    series: 'Playano Engineering',
    image: assetUrl('blog/retrieval-methods-cover.jpg'),
    excerpt:
      'We benchmarked a production RAG service across LOOKUP, AUDIT, and SYNTHESIS modes. See how k-size, latency, and recall trade off, and why bigger k is not always better.',
    keywords: ['RAG retrieval benchmark', 'retrieval methods', 'LLM RAG', 'hybrid search', 'semantic search benchmark'],
    content: (
      <>
        <p>
          Every LLM-powered system eventually needs a retrieval layer. The question is not whether to add RAG. It is
          how much retrieval to pay for. Retrieve too little and the model hallucinates or misses the right file.
          Retrieve too much and you burn tokens, latency, and context window on noise.
        </p>

        <h2>The problem</h2>
        <p>
          We run a production retrieval service over internal rules and project docs. We wanted a reproducible way to
          decide how many chunks to retrieve per query, whether increasing k actually improves answer quality, and
          when a full-file read is worth the extra cost.
        </p>
        <p>
          We defined ten ground-truth queries across three retrieval usage modes: <strong>LOOKUP</strong>
          (&quot;where is the backup script?&quot; &mdash; top-k chunks only), <strong>AUDIT</strong> (&quot;does this
          project follow the rules?&quot; &mdash; top-k chunks plus full-file reads of matches), and{' '}
          <strong>SYNTHESIS</strong> (&quot;how should we back up Postgres per our rules?&quot; &mdash; multi-repo
          search plus full-file reads). For each query we ran retrieval at k=3, k=5, and k=10 against the production
          service and recorded recall@k, term hit rate, mean reciprocal rank (MRR), and latency.
        </p>

        <ChartZoom><RetrievalDepthChart /></ChartZoom>

        <h2>What stands out</h2>
        <p>
          Recall did not improve after k=3 &mdash; all three configs found the expected file for 9 of 10 queries. The
          missing query returned the right term in a different file than the ground truth expected, which is a
          chunking/ground-truth issue, not a retrieval-depth issue. Term hit rate only improved at k=10: even when
          the right file was retrieved, the specific phrase needed was sometimes buried in a chunk outside the top 5.
          MRR held flat across all three depths.
        </p>
        <p>
          <em>
            This is a single run per k on a 10-query set biased toward our own docs &mdash; a documented case, not a
            statistically tested claim; see &quot;What this post does not answer&quot; below.
          </em>
        </p>

        <ChartZoom><RetrievalLatencyChart /></ChartZoom>

        <p>
          Latency has a cold-start cliff. The first request of each query took roughly 600&ndash;2700ms because the
          embedding call was not warmed. Subsequent requests for the same query dropped to roughly 250&ndash;350ms.
          Real-world latency in a warm system is closer to the k=5/k=10 numbers than the k=3 average.
        </p>

        <h2>The idea</h2>
        <p>
          These findings shaped our own retrieval configuration: LOOKUP uses k=3, which is enough for factual
          &quot;where is X&quot; questions. AUDIT uses k=5 and reads the matched subsections in full. SYNTHESIS uses
          k=5 per repo, merges results, and reads full files when the semantic index maps to a rule range. Bigger k
          is not free and not always better &mdash; the win from k=10 was marginal (+10% term hit) while the cost in
          tokens and context window was roughly double.
        </p>

        <h2>What engineers actually see</h2>
        <p>
          Running this benchmark gets you a small ground-truth query set, a batch runner, and a report with recall,
          term hit rate, MRR, and latency broken out by k. It turns &quot;how much should we retrieve for this kind
          of question?&quot; into a measured answer instead of a guess. (This work connects to{' '}
          <Link to="/rag-context-injection-benchmark" className="text-[#FD9800] underline underline-offset-2">
            how much of what is retrieved should go into the prompt
          </Link>
          . Browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>Why this matters</h2>
        <p>
          Retrieval depth is a dial, not a default. Measuring it against real questions, with real files expected
          back, is what turns a RAG configuration from a guess into an engineering decision.
        </p>

        <h2>What this post does not answer</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>We did not test keyword-only vs semantic-only vs hybrid retrieval; the production service currently exposes hybrid search only.</li>
          <li>We did not measure end-to-end LLM answer correctness, only retrieval quality. A separate LLM-as-judge benchmark would close that gap.</li>
          <li>The query set is small (10 questions) and biased toward our own docs. Scale and domain-transfer are the next tests.</li>
          <li>Each k value was run once; there is no confidence interval or repeated-trial estimate behind these numbers.</li>
        </ul>

        <p className="mt-8">
          <a href="https://playanoedu.com" className="inline-block bg-[#FD9800] hover:bg-[#e08700] text-black font-semibold px-6 py-3 rounded-lg">
            Read more engineering benchmarks at playanoedu.com
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is more retrieval always better?', a: 'No. In this benchmark, recall and MRR held flat past k=3, and the term-hit gain from k=10 was marginal against roughly double the token cost.' },
      { q: 'Why three different modes instead of one k value?', a: 'LOOKUP, AUDIT, and SYNTHESIS ask fundamentally different questions — a quick factual lookup does not need the same depth as reconstructing a full audit trail.' },
      { q: 'Does latency scale with k?', a: 'Not monotonically in this run. The k=3 average was inflated by a cold-start embedding call; warm requests were faster and comparable across k=5 and k=10.' },
      { q: 'How does this relate to context injection?', a: 'This post measures how much to retrieve. A related benchmark measures how much of what is retrieved should actually go into the prompt — see the linked context-injection post.' },
    ],
    sources:
      'Internal RAG retrieval benchmark, 10 ground-truth queries across LOOKUP/AUDIT/SYNTHESIS modes, run 2026-08-11 against a production retrieval service. Single run per k; no repeated trials or confidence interval computed.',
  },
];
